import type { RuntimeConfigValueRow } from '../api';
import type { RuntimeConfigValueDraft } from '../types/dialogs';

// Mirrors libs/backend/services-nest/runtime/contracts/src/validation/runtime-config-value.validation.ts
// Client-side copy so invalid keys are caught before round-tripping to the
// server — the backend re-validates independently and is the actual source
// of truth. Keep both lists in sync if either changes.
export const RUNTIME_CONFIG_VALUE_KEY_MAX_LENGTH = 128;
export const RUNTIME_CONFIG_VALUE_MAX_LENGTH = 4096;

export const RUNTIME_CONFIG_VALUE_KEY_PATTERN = /^[a-zA-Z_$][a-zA-Z0-9_$]*$/;

export const RESERVED_RUNTIME_CONFIG_VALUE_KEYS: ReadonlySet<string> = new Set([
  '__proto__',
  'constructor',
  'prototype',
  'eval',
  'Function',
  'window',
  'self',
  'top',
  'parent',
  'globalThis',
  'location',
  'document',
  'history',
  'navigator',
  'localStorage',
  'sessionStorage',
  'fetch',
  'XMLHttpRequest',
  'WebSocket',
  'frames',
  'opener',
  'name',
]);

export const MANAGED_RUNTIME_CONFIG_VALUE_KEYS: ReadonlySet<string> = new Set([
  'expressApiHostname',
  'staticCdnPrefix',
  'analyticsEnabled',
  'analyticsPageviewsEnabled',
  'posthogApiKey',
  'posthogHost',
  'otelEnabled',
  'otelCollectorUrl',
]);

export function validateRuntimeConfigValueKey(key: string): string | null {
  if (!key) {
    return 'Key is required.';
  }
  if (key.length > RUNTIME_CONFIG_VALUE_KEY_MAX_LENGTH) {
    return `Key must be ${RUNTIME_CONFIG_VALUE_KEY_MAX_LENGTH} characters or fewer.`;
  }
  if (!RUNTIME_CONFIG_VALUE_KEY_PATTERN.test(key)) {
    return 'Key must be a valid identifier (letters, digits, _ or $, not starting with a digit).';
  }
  if (RESERVED_RUNTIME_CONFIG_VALUE_KEYS.has(key)) {
    return `"${key}" is a reserved browser/JS global and can't be used as a key.`;
  }
  return null;
}

export type RuntimeConfigValueType = 'string' | 'boolean' | 'number' | 'json';

export function validateRuntimeConfigValue(
  value: string,
  valueType: RuntimeConfigValueType = 'string'
): string | null {
  if (value.length > RUNTIME_CONFIG_VALUE_MAX_LENGTH) {
    return `Value must be ${RUNTIME_CONFIG_VALUE_MAX_LENGTH} characters or fewer.`;
  }
  if (valueType === 'boolean' && value !== 'true' && value !== 'false') {
    return 'Boolean values must be true or false.';
  }
  if (valueType === 'number' && (value.trim() === '' || !Number.isFinite(Number(value)))) {
    return 'Enter a valid number.';
  }
  if (valueType === 'json') {
    try {
      JSON.parse(value);
    } catch {
      return 'Enter valid JSON.';
    }
  }
  return null;
}

/**
 * Turns a saved rule's values into editable dialog rows. Masked values are
 * never pre-filled with their plaintext — an admin who wants to change one
 * has to explicitly retype it (see RuntimeConfigValueListEditor's "Change"
 * affordance).
 */
export function toRuntimeConfigValueDrafts(
  values: RuntimeConfigValueRow[] | undefined
): RuntimeConfigValueDraft[] {
  return (values || []).map((row) => ({
    runtimeConfigValueId: row.runtimeConfigValueId,
    key: row.key,
    value: row.masked ? '' : row.value,
    valueType: row.valueType,
    masked: row.masked,
    valueTouched: !row.masked,
  }));
}
